import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import type { CalendarEvent } from "../../api";
import {
  weekDaysSun,
  dateKey,
  isToday,
  eventChunkForDay,
  ptHoursSinceMidnight,
  formatHour,
  isoAtPtHourOnDay,
  PT_TZ,
} from "../../lib/calendar-util";

interface Props {
  anchor: Date;
  events: CalendarEvent[];
  onEditEvent: (event: CalendarEvent) => void;
  /** Called when user clicks an empty time slot. Receives an ISO UTC start
   *  pre-filled with the clicked hour on the clicked day. */
  onCreateAt: (startISO: string, allDay: boolean) => void;
}

const ROW_PX = 40; // height per hour-row — must match .calendar-week-hour-row in CSS
const DEFAULT_SCROLL_HOUR = 6;

/**
 * 7-column week grid, Sunday-first. Two distinct sections stacked vertically:
 *  - Day header + all-day row (banded under headers, multi-day events span
 *    visually across the columns they touch).
 *  - 24-hour time rail with absolutely-positioned event blocks per column.
 *    Initial scroll lands at 6 AM PT; off-hours are accessible by scroll.
 *
 * Cross-midnight timed events render as per-day chips (the simpler model
 * agreed in C.1.6 planning) — clicking either chunk opens the same event's
 * edit form.
 */
export function CalendarWeekView({
  anchor,
  events,
  onEditEvent,
  onCreateAt,
}: Props) {
  const days = useMemo(() => weekDaysSun(anchor), [anchor]);
  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // Scroll to 6 AM on mount and whenever the week changes. Uses
  // useLayoutEffect (not useEffect) so the position is set BEFORE the
  // browser paints — without this you'd see a midnight flash before the
  // scroll lands. rAF chaser handles the rare case where layout settles
  // one frame after the layout effect (e.g. fonts still loading).
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const top = DEFAULT_SCROLL_HOUR * ROW_PX;
    el.scrollTop = top;
    const id = requestAnimationFrame(() => {
      if (el) el.scrollTop = top;
    });
    return () => cancelAnimationFrame(id);
  }, [anchor]);

  // Tick every 60 s so the "now" line moves in real-time.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Split events into all-day vs timed up front. All-day events get rendered
  // as a horizontal span; timed events are placed inside each day column.
  const allDay = events.filter((e) => e.allDay);
  const timed = events.filter((e) => !e.allDay);

  const dayKeys = days.map(dateKey);
  const todayCol = days.findIndex(isToday);

  return (
    <div className="calendar-week">
      {/* Header row: weekday + date number per column. */}
      <div className="calendar-week-header">
        <div className="calendar-week-gutter">PT</div>
        {days.map((d, i) => (
          <div
            key={dayKeys[i]}
            className={clsx("calendar-week-daycol-head", isToday(d) && "today")}
          >
            <div className="calendar-week-weekday">{weekdayShort(d)}</div>
            <div className="calendar-week-daynum">{dayNum(d)}</div>
          </div>
        ))}
      </div>

      {/* All-day band — events that span multiple days render as a single
          horizontal bar across the columns they touch. */}
      <div className="calendar-week-allday">
        <div className="calendar-week-gutter">ALL DAY</div>
        <div className="calendar-week-allday-grid">
          {allDay.map((evt) => {
            const span = computeAllDaySpan(evt, dayKeys);
            if (!span) return null;
            return (
              <div
                key={evt.id}
                className={clsx(
                  "calendar-week-allday-chip",
                  `priority-${evt.priority}`,
                )}
                style={{
                  gridColumn: `${span.startCol + 1} / span ${span.length}`,
                }}
                title={evt.title}
                onClick={() => onEditEvent(evt)}
              >
                {evt.title}
              </div>
            );
          })}
        </div>
      </div>

      {/* Time rail: 24 hours, scrollable, with a column per day. */}
      <div ref={scrollerRef} className="calendar-week-scroller">
        <div className="calendar-week-grid">
          <div className="calendar-week-gutter-col">
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} className="calendar-week-gutter-cell">
                {formatHour(h)}
              </div>
            ))}
          </div>
          {days.map((day, di) => (
            <DayColumn
              key={dayKeys[di]}
              day={day}
              isTodayCol={di === todayCol}
              events={timed}
              onEdit={onEditEvent}
              onCreateAt={onCreateAt}
              nowTick={nowTick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function DayColumn({
  day,
  isTodayCol,
  events,
  onEdit,
  onCreateAt,
  nowTick: _nowTick,
}: {
  day: Date;
  isTodayCol: boolean;
  events: CalendarEvent[];
  onEdit: (e: CalendarEvent) => void;
  onCreateAt: (startISO: string, allDay: boolean) => void;
  nowTick: number;
}) {
  // Each event becomes 0..1 chunks for this day; cross-midnight events get
  // two chunks (one per day they touch) — both link back to the same row.
  const chunks = events.flatMap((e) => {
    const c = eventChunkForDay(e.startAt, e.endAt, day);
    if (!c) return [];
    return [{ ...c, event: e }];
  });

  const handleColumnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only trigger on the empty grid — children call stopPropagation.
    if (e.target !== e.currentTarget) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const hour = Math.max(0, Math.min(23, Math.floor(y / ROW_PX)));
    const iso = isoAtPtHourOnDay(dateKey(day), hour);
    onCreateAt(iso, false);
  };

  // Now-line position (only rendered in today's column).
  const nowHr = isTodayCol ? ptHoursSinceMidnight(new Date().toISOString()) : null;

  return (
    <div
      className={clsx("calendar-week-daycol", isTodayCol && "today")}
      style={{ height: `${24 * ROW_PX}px` }}
      onClick={handleColumnClick}
    >
      {Array.from({ length: 24 }, (_, h) => (
        <div key={h} className="calendar-week-hour-row" style={{ top: `${h * ROW_PX}px` }} />
      ))}
      {chunks.map(({ event, startHour, endHour }) => (
        <div
          key={`${event.id}-${startHour}`}
          className={clsx(
            "calendar-week-event",
            `priority-${event.priority}`,
            chunks.length > 1 && "side-by-side",
          )}
          style={{
            top: `${startHour * ROW_PX}px`,
            height: `${Math.max(20, (endHour - startHour) * ROW_PX)}px`,
          }}
          onClick={(e) => {
            e.stopPropagation();
            onEdit(event);
          }}
          title={`${event.title}${event.dealName ? ` · ${event.dealName.toUpperCase()}` : ""}`}
        >
          <div className="calendar-week-event-time">
            {formatHourMin(event.startAt)}
          </div>
          <div className="calendar-week-event-title">{event.title}</div>
        </div>
      ))}
      {nowHr !== null && (
        <div
          className="calendar-week-now-line"
          style={{ top: `${nowHr * ROW_PX}px` }}
        >
          <span className="calendar-week-now-dot" />
        </div>
      )}
    </div>
  );
}

/** All-day events: determine the [startCol, length] visual span. */
function computeAllDaySpan(
  evt: CalendarEvent,
  dayKeys: string[],
): { startCol: number; length: number } | null {
  // start day-key
  const startKey = isoToPTDateKey(evt.startAt);
  // Outlook's all-day events: end is the day AFTER the last day (midnight
  // boundary). Subtract one day for the visual range.
  const inclusiveEnd = new Date(new Date(evt.endAt).getTime() - 1).toISOString();
  const endKey = isoToPTDateKey(inclusiveEnd);

  let startCol = dayKeys.indexOf(startKey);
  let endCol = dayKeys.indexOf(endKey);
  // Clamp partials at the week edges.
  if (startCol === -1 && endCol === -1) {
    // Event entirely outside this week.
    if (startKey > dayKeys[6] || endKey < dayKeys[0]) return null;
    startCol = 0;
    endCol = 6;
  } else {
    if (startCol === -1) startCol = 0;
    if (endCol === -1) endCol = 6;
  }
  return { startCol, length: Math.max(1, endCol - startCol + 1) };
}

function isoToPTDateKey(iso: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: PT_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day}`;
}

function weekdayShort(d: Date): string {
  return d.toLocaleDateString("en-US", { weekday: "short", timeZone: PT_TZ });
}
function dayNum(d: Date): string {
  return d.toLocaleDateString("en-US", { day: "numeric", timeZone: PT_TZ });
}
function formatHourMin(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: PT_TZ,
  });
}
